// useNivelesUsuario.js
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useLogout from "./useLogout";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const useNivelesUsuario = () => {
  const [niveles, setNiveles] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [mostrarMensaje, setMostrarMensaje] = useState(false);
  const [usuario, setUsuario] = useState(null);

  const [nivelPositions] = useState({
    0: { x: 60, y: 160 },
    1: { x: 300, y: 140 },
    2: { x: 580, y: 140 },
    3: { x: 730, y: 50 },
    4: { x: 900, y: 10 },
  });

  const [banderaResultadosPosition] = useState({ x: 1006, y: -20 });
  const navigate = useNavigate();
  const logout = useLogout();

  const mostrarVentana = (msg) => {
    setMensaje(msg);
    setMostrarMensaje(true);
  };
  const cerrarVentana = () => {
    setMostrarMensaje(false);
    setMensaje("");
  };

  const cargarNiveles = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const usuarioGuardado = JSON.parse(localStorage.getItem("usuario"));
      setUsuario(usuarioGuardado);

      // ✅ Detectar Docente de forma robusta
      const rolGrupo = (usuarioGuardado?.rolGrupo || "").toLowerCase();
      const rolPlano = (usuarioGuardado?.rol || "").toLowerCase();
      const isDocente =
        rolGrupo === "docente" ||
        rolPlano.includes("docente") ||
        rolPlano.includes("teacher") ||
        rolPlano.includes("profesor") ||
        rolPlano.includes("profesora");

      const { data } = await axios.get(`${API_BASE_URL}/api/niveles/usuario`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        params: { t: Date.now() },
      });

      // Normaliza base
      let rows = (Array.isArray(data) ? data : []).map((n) => ({
        ...n,
        completado: Number(n.completado) === 1 || n.completado === true,
      }));

      // Orden ascendente por 'orden'
      rows.sort((a, b) => (a.orden ?? 9999) - (b.orden ?? 9999));

      // 🔧 RECORTE CORRECTO POR ROL
      // - Docente: excluir diagnóstico (orden = 1) y tomar EXACTAMENTE 5 (orden 2..6)
      // - Otros: excluir diagnóstico y tomar 4 (orden 2..5)
      if (isDocente) {
        rows = rows.filter((n) => n.orden !== 1).slice(0, 5);
      } else {
        rows = rows.filter((n) => n.orden !== 1).slice(0, 4);
      }

      // 🔓 Desbloqueo por índice visible (en la lista ya filtrada)
      const byIndex = rows.map((n, idx, arr) => {
        const desbloqueado = idx === 0 ? true : !!arr[idx - 1].completado;
        return { ...n, desbloqueado };
      });

      // ⭐ Único activo = primer (desbloqueado && !completado)
      const nextActiveIndex = byIndex.findIndex(
        (n) => n.desbloqueado && !n.completado
      );
      const procesados = byIndex.map((n, idx) => ({
        ...n,
        esActivo: idx === nextActiveIndex,
      }));

      setNiveles(procesados);
    } catch (error) {
      console.error(
        "Error detallado al cargar niveles:",
        error.response ? error.response.data : error.message
      );
      mostrarVentana(
        "Error al cargar los niveles. Por favor, cierre sesión e intente de nuevo."
      );
    }
  }, []);

  useEffect(() => {
    cargarNiveles();
  }, [cargarNiveles]);

  const verificarPreguntas = async (nivelId) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${API_BASE_URL}/api/preguntas/nivel/${nivelId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.length > 0;
    } catch (error) {
      console.error("Error al verificar preguntas:", error);
      return false;
    }
  };

  const todosNivelesCompletados = () => {
    // Ahora 'niveles' ya viene recortado por rol (4 o 5).
    return niveles.length > 0 && niveles.every((n) => n.completado);
  };

  const handleNivelClick = async (nivel) => {
    // fallback: desbloqueo también si el anterior está completo
    const nivelesOrdenados = [...niveles].sort((a, b) => a.orden - b.orden);
    const idx = nivelesOrdenados.findIndex((n) => n.id === nivel.id);
    const prevCompleto =
      idx === 0 ? true : nivelesOrdenados[idx - 1].completado;
    const isUnlocked = nivel.desbloqueado || prevCompleto;

    if (!isUnlocked) {
      mostrarVentana("Debes completar el nivel anterior primero.");
      return;
    }

    if (nivel.completado) {
      mostrarVentana("Nivel ya completado, ingrese al siguiente nivel.");
      return;
    }

    const tienePreguntas = await verificarPreguntas(nivel.id);
    if (!tienePreguntas) {
      mostrarVentana(
        "SIN PREGUNTAS REGISTRADAS. Contacte con un Administrador."
      );
      return;
    }

    navigate(`/contenido-multimedia/${nivel.id}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return {
    niveles,
    mensaje,
    mostrarMensaje,
    nivelPositions,
    banderaResultadosPosition,
    handleNivelClick,
    handleLogout,
    todosNivelesCompletados,
    mostrarVentana,
    cerrarVentana,
    navigate,
    usuario,
    refetch: cargarNiveles,
  };
};

export default useNivelesUsuario;
